import { diff } from 'jsondiffpatch';
import { sortBy } from 'lodash';
import { logExceptionOnce } from '@hungryroot/o11y';
import {
  type Cart,
  type CartPairing,
  type CartProduct,
  type Bundle,
  type ProductBundle,
  type PairingBundle,
  type OrderDetail,
} from '@hungryroot/rest';

export function cartHasChanges(
  originalCart: Cart | null,
  nextCart: Cart
): boolean {
  // original cart isn't loaded yet?
  if (originalCart === null) {
    return false;
  }

  return diff(originalCart, nextCart) !== undefined;
}

export function calculateThresholdDifference(
  cartSaveThreshold: number,
  cart: Pick<Cart, 'points'>
) {
  const { points: cartPoints } = cart;
  return cartSaveThreshold - cartPoints;
}

export function getCartProductById(
  cart: Cart,
  productId: number
): CartProduct | undefined {
  const { products, order_id: orderId } = cart;
  const product = products.find(({ id }) => {
    return id === productId;
  });

  if (product === undefined) {
    logExceptionOnce('Unable to find product', {
      productId,
      orderId,
      key: 'getCartProductById',
    });
  }

  return product;
}

export function findCartPairingBundle(
  cart: Cart,
  pairingId: number
): PairingBundle | undefined {
  const bundlePairings = getCartPairingBundles(cart);
  const match = bundlePairings.find(({ pairing: { id: cartPairingId } }) => {
    return cartPairingId === pairingId;
  });

  return match;
}

export function getCartProductBundle(
  cart: Cart,
  productId: number
): ProductBundle | undefined {
  const productBundles = getCartProductBundles(cart);
  const match = productBundles.find(({ order_details }) => {
    return order_details.find(({ product }) => {
      return product === productId;
    });
  });

  if (match === undefined) {
    logExceptionOnce('Unable to find product', {
      productId,
      orderId: cart.order_id,
      key: 'getCartProductBundle',
    });
  }
  return match;
}

export function getCartPairingBundles(cart: Cart): PairingBundle[] {
  const { bundles: cartBundles } = cart;
  const bundles: PairingBundle[] = [];
  cartBundles.forEach((bundle) => {
    const { pairing: bundlePairing } = bundle;
    if (bundlePairing === null) {
      return;
    }
    bundles.push(bundle);
  });
  return bundles;
}

export function getBundleDiffBetweenCarts(
  cart: Cart,
  previousCart: Cart | null,
  pairing: CartPairing
) {
  const { bundles: currentBundles } = cart;
  let previousBundles: Bundle[] = [];

  if (previousCart !== null) {
    const { bundles } = previousCart;
    previousBundles = bundles;
  }

  const bundlesNotInPrev = currentBundles.filter(
    (currentBundle) =>
      !previousBundles.some(
        (previousBundle: Bundle) => previousBundle?.id === currentBundle?.id
      )
  );

  const pairingBundle = bundlesNotInPrev.find((bundle) => {
    return bundle.pairing !== null && bundle.pairing.id === pairing.id;
  }) as PairingBundle | undefined;

  return pairingBundle;
}

export function getCartPairingBundle(
  cart: Cart,
  pairingId: number
): PairingBundle {
  const bundlePairings = getCartPairingBundles(cart);
  const match = bundlePairings.find(({ pairing: { id: cartPairingId } }) => {
    return cartPairingId === pairingId;
  });
  if (match === undefined) {
    throw new Error(`Unable to find pairing bundle "${pairingId}"`);
  }
  return match;
}

export function getLatestCartPairingBundle(
  cart: Cart,
  pairingId: number
): PairingBundle {
  const bundlePairings = getCartPairingBundles(cart);
  let matchBundle: PairingBundle | null = null;
  bundlePairings.forEach((bundle) => {
    const {
      id: bundleId,
      pairing: { id: cartPairingId },
    } = bundle;
    if (cartPairingId !== pairingId) {
      return;
    }
    if (matchBundle === null || bundleId > matchBundle.id) {
      matchBundle = bundle;
      return;
    }
  });

  if (matchBundle === null) {
    throw new Error(`Unable to find pairing bundle "${pairingId}"`);
  }
  return matchBundle;
}

export function isMeal(product: CartProduct) {
  return !!product.is_meal;
}

/**
 * Returns a count of the number of meals in the cart using the `MEAL_TAGS` constant
 */
export function getNumberOfMeals(cart: Cart): number {
  let count = 0;
  const counted: Record<string, true> = {};
  const productBundles = getCartProductBundles(cart);
  productBundles.forEach(({ order_details }) => {
    order_details.forEach(({ product }) => {
      const cartProduct = getCartProductById(cart, product);
      if (
        cartProduct === undefined ||
        !isMeal(cartProduct) ||
        counted[product] === true
      ) {
        return;
      }

      count += getProductCount(cart, product);
      counted[product] = true;
    });
  });

  return count;
}

export function getNumberOfGroceries(cart: Cart): number {
  let count = 0;
  const counted: Record<string, true> = {};
  const productBundles = getCartProductBundles(cart);
  productBundles.forEach(({ order_details }) => {
    order_details.forEach(({ product }) => {
      const cartProduct = getCartProductById(cart, product);
      if (
        counted[product] === true ||
        (cartProduct !== undefined && isMeal(cartProduct))
      ) {
        return;
      }
      count += getProductCount(cart, product);
      counted[product] = true;
    });
  });

  return count;
}

export function getProductCount(cart: Cart, productId: number): number {
  let count = 0;

  cart.bundles.forEach((bundle) => {
    const { pairing: bundlePairing, order_details } = bundle;
    if (bundlePairing !== null) {
      return;
    }
    order_details.forEach(({ product: detailsProductId }) => {
      if (detailsProductId !== productId) {
        return;
      }

      count += 1;
    });
  });

  return count;
}

export function getTotalPairingCounts(cart: Cart): number {
  let count = 0;
  const counted: Record<string, true> = {};
  const pairingBundles = getCartPairingBundles(cart);
  pairingBundles.forEach(({ pairing }) => {
    const { id: pairingId } = pairing;
    if (counted[pairingId] === true) {
      return;
    }
    count += getPairingCount(cart, pairingId);
    counted[pairingId] = true;
  });
  return count;
}

export function getPairingCount(cart: Cart, pairingId: number): number {
  let count = 0;

  cart.bundles.forEach((bundle) => {
    const { pairing: bundlePairing } = bundle;
    if (bundlePairing === null) {
      return;
    }
    const { id: bundlePairingId } = bundlePairing;
    if (bundlePairingId !== pairingId) {
      return;
    }
    count += 1;
  });

  return count;
}

export function getCartProductBundles(cart: Cart): ProductBundle[] {
  const { bundles: cartBundles } = cart;
  const bundles: ProductBundle[] = [];
  cartBundles.forEach((bundle) => {
    const { pairing: bundlePairing } = bundle;
    if (bundlePairing !== null) {
      return;
    }
    bundles.push(bundle);
  });
  return bundles;
}

export function getLooseCartProducts(cart: Cart): CartProduct[] {
  const products: CartProduct[] = [];
  cart.bundles.forEach((bundle) => {
    if (bundle.pairing !== null) {
      return;
    }

    bundle.order_details.forEach(({ product: productId }) => {
      const product = getCartProductById(cart, productId);
      if (product !== undefined) {
        products.push(product);
      }
    });
  });

  return products;
}

export interface GroupedPairingBundle {
  count: number;
  pairing: CartPairing;
  orderDetails: GroupedBundleOrderDetails[];
  highestBundleId: number;
  originalOrderDetails: PairingBundle['order_details'];
  points: string;
  bundleIds: number[];
}
/*
 * This function groupes *PAIRING* bundles based on the pairing id
 * PLUS ingredient modifications.
 *
 * For example, this function will return
 * an array with 1 item if there are two pairings in the cart with
 * identical order_details.
 *
 * This function will return 2 items if there are two pairings with the same ID,
 * but have different order_details
 */
export function getGroupedPairingBundles(cart: Cart): GroupedPairingBundle[] {
  const pairingBundles = getCartPairingBundles(cart);

  const map = pairingBundles.reduce(
    (seed, pairingBundle) => {
      const { pairing, id: bundleId, order_details, points } = pairingBundle;
      const { id: pairingId } = pairing;
      const orderDetailIds = order_details.map(({ product }) => product).sort();
      const key = `${pairingId}-${orderDetailIds.join('-')}`;
      let item = seed[key];
      if (item === undefined) {
        item = seed[key] = {
          bundleIds: [],
          count: 0,
          pairing,
          originalOrderDetails: order_details,
          orderDetails: getGroupedBundleOrderDetails(cart, pairingBundle),
          highestBundleId: bundleId,
          points,
        };
      }

      if (bundleId > item.highestBundleId) {
        item.highestBundleId = bundleId;
      }
      item.bundleIds = sortBy([...item.bundleIds, bundleId]);
      item.count += 1;

      return seed;
    },
    {} as Record<string, GroupedPairingBundle>
  );

  return Object.values(map);
}

interface GroupedBundleOrderDetails {
  product: CartProduct;
  quantity: number;
}

export function getGroupedBundleOrderDetails(
  cart: Cart,
  bundle: Bundle
): Array<GroupedBundleOrderDetails> {
  const { order_details } = bundle;
  const map: Record<string, GroupedBundleOrderDetails> = {};
  order_details.forEach(({ product }) => {
    let item = map[product];
    if (item === undefined) {
      const cartProduct = getCartProductById(cart, product);
      if (cartProduct !== undefined) {
        item = map[product] = {
          quantity: 0,
          product: cartProduct,
        };
      }
    }

    item.quantity += 1;
  });

  return Object.values(map);
}

interface GroupedIngredient {
  ingredient_id: number;
  amount: number;
}

export function pairingIsModified(
  orderDetails: OrderDetail[],
  neededIngredients: CartPairing['needed_ingredients']
) {
  const map: GroupedIngredient[] = [];
  orderDetails.forEach((ingredient) => {
    let item = map[ingredient.product];
    if (item === undefined) {
      item = map[ingredient.product] = {
        ingredient_id: ingredient.product,
        amount: 0,
      };
    }
    if (ingredient.quantity === 0) item.amount += 0.5;
    else item.amount += 1;
  });
  const groupedOrderDetails = Object.values(map);

  if (groupedOrderDetails.length !== neededIngredients.length) return true;

  return !groupedOrderDetails.every((ingredient: GroupedIngredient) =>
    neededIngredients.some((pairingIngredient: GroupedIngredient) =>
      Object.keys(ingredient).every(
        (key) =>
          ingredient[key as keyof GroupedIngredient] ===
          pairingIngredient[key as keyof GroupedIngredient]
      )
    )
  );
}

export type IsNotAddableParams = {
  maxSameItem: number;
  volumeLimit?: number;
  coldVolumeLimit?: number;
  cart: Cart;
} & (
  | {
      pairing: undefined;
      product: {
        id: number;
        is_ambient: boolean;
        volume: number;
        is_merchandized: boolean;
      };
    }
  | {
      pairing: {
        id: number;
        ingredients: Array<{
          id: number;
          amount: number;
          is_ambient: boolean;
          volume: number;
        }>;
      };
      product: undefined;
    }
);

export function isNotAddable(params: IsNotAddableParams) {
  const { volumeLimit, coldVolumeLimit, maxSameItem, pairing, cart } = params;

  const { bundles, cold_volume: boxColdVolume, volume: boxVolume } = cart;
  const recipeSkuMap: Record<string, number> = {};
  const sauceMap: Record<string, number> = {};
  bundles.forEach(({ order_details }) => {
    order_details.forEach(({ product: productId }) => {
      const map = recipeSkuMap[productId];
      if (map === undefined) {
        recipeSkuMap[productId] = 0;
      }

      recipeSkuMap[productId] += 1;
    });
  });

  let volume = 0;
  let coldVolume = 0;
  let qty = 0;

  if (pairing === undefined) {
    const { product } = params;
    const {
      id: productId,
      is_ambient: isAmbient,
      volume: itemVolume,
      is_merchandized: isMerchandized = true,
    } = product;

    if (isMerchandized === false) return true;
    volume += itemVolume;
    if (!isAmbient) {
      coldVolume += itemVolume;
    }
    const quantity = getProductCount(cart, productId);
    if (quantity) {
      qty += quantity + 1;
      if (qty > maxSameItem) {
        return true;
      }
    }
  } else {
    const { id: pairingId, ingredients } = pairing;
    const recipeCount = getPairingCount(cart, pairingId);
    if (recipeCount >= maxSameItem) {
      return true;
    }
    for (const sku of ingredients) {
      const {
        id: skuId,
        amount: addableAmount,
        is_ambient: isAmbient,
        volume: skuVolume,
      } = sku;
      let currentAmount = recipeSkuMap[skuId] || 0;
      currentAmount += addableAmount;
      if (Number.isInteger(addableAmount)) {
        volume += skuVolume;
        if (!isAmbient) {
          coldVolume += skuVolume;
        }
        if (currentAmount > maxSameItem) {
          return true;
        }
      } else {
        const sauceAmount = sauceMap[skuId];
        if ((sauceAmount && Number.isInteger(sauceAmount)) || !sauceAmount) {
          const currentAmount = addableAmount + Math.ceil(sauceAmount);
          volume += skuVolume;
          if (!isAmbient) {
            coldVolume += skuVolume;
          }
          if (currentAmount > maxSameItem) {
            return true;
          }
        }
      }
    }
  }

  return (
    (volumeLimit !== undefined && volume + boxVolume >= volumeLimit) ||
    qty > maxSameItem ||
    (coldVolumeLimit !== undefined &&
      coldVolume + boxColdVolume >= coldVolumeLimit)
  );
}
